import { Alert, Button, DatePicker, Drawer, Flex, Pagination, Spin } from 'antd';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';

import { Schemas } from '@api-client/generated/types';
import { useMembershipJournal } from '@hooks-api';

import MembershipJournalEvent from './MembershipJournalEvent';
import * as S from './styled';

type EventType = Schemas.MembershipEventRecordDto['type'];

/** Minimal member shape used to resolve a target id to a display name. */
export interface JournalKnownMember {
  userId: string;
  firstName: string;
  lastName: string;
}

type Props = {
  open: boolean;
  companyId: string;
  companyName?: string;
  knownMembers?: JournalKnownMember[];
  onClose: () => void;
};

const TYPE_OPTIONS: Array<{ label: string; value: EventType }> = [
  { label: 'Access granted', value: 'created' },
  { label: 'Access revoked', value: 'deleted' },
  { label: 'Permissions changed', value: 'updated' },
];

const MembershipJournalDrawer = ({
  open,
  companyId,
  companyName,
  knownMembers = [],
  onClose,
}: Props) => {
  const [type, setType] = useState<EventType | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [page, setPage] = useState(1);

  const { events, metadata, isLoading, isError, refetch } = useMembershipJournal(
    companyId,
    {
      type,
      dateFrom: range?.[0].startOf('day').toISOString(),
      dateTo: range?.[1].endOf('day').toISOString(),
      page,
    }
  );

  const nameByUserId = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of knownMembers) {
      map.set(member.userId, `${member.firstName} ${member.lastName}`.trim());
    }
    return map;
  }, [knownMembers]);

  // Revoked members are no longer in the roster, so their name can't be
  // resolved — label them neutrally rather than exposing a raw id.
  const targetName = (userId: string) =>
    nameByUserId.get(userId) ?? 'Former member';

  const resetToFirstPage = () => setPage(1);

  return (
    <Drawer
      title={companyName ? `Access history — ${companyName}` : 'Access history'}
      open={open}
      onClose={onClose}
      width={480}
      destroyOnClose
    >
      <S.Filters gap={12} wrap="wrap">
        <S.TypeSelect
          allowClear
          placeholder="All events"
          value={type}
          options={TYPE_OPTIONS}
          onChange={(value) => {
            setType(value);
            resetToFirstPage();
          }}
        />
        <DatePicker.RangePicker
          value={range}
          onChange={(dates) => {
            setRange(dates as [Dayjs, Dayjs] | null);
            resetToFirstPage();
          }}
        />
      </S.Filters>

      {isLoading ? (
        <Flex justify="center">
          <Spin />
        </Flex>
      ) : isError ? (
        // A failed request is not an empty journal — surface the error and let
        // the admin retry rather than implying no audit history exists.
        <Alert
          type="error"
          showIcon
          message="Failed to load access history"
          description="Something went wrong while loading the journal."
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : events.length ? (
        <Flex vertical>
          {events.map((event) => (
            <MembershipJournalEvent
              key={`${event.targetUserId}:${event.createdAt}:${event.type}`}
              event={event}
              targetName={targetName(event.targetUserId)}
            />
          ))}
          {metadata && metadata.totalPages > 1 && (
            <S.PaginationRow justify="center">
              <Pagination
                simple
                current={page}
                pageSize={metadata.perPage}
                total={metadata.totalRecords}
                onChange={setPage}
              />
            </S.PaginationRow>
          )}
        </Flex>
      ) : (
        <S.Empty>No access history yet</S.Empty>
      )}
    </Drawer>
  );
};

export default MembershipJournalDrawer;
